import React from 'react';
import { NamedProps } from 'react-select';
import styled, { useTheme } from 'styled-components';
import {
  getInputStateColors,
  getOutlinedInputStateColors,
} from '../components/input-skeleton/input-skeleton.styled';

export const SelectSC = styled.div`
  min-width: 170px;

  .ui-select {
    max-width: 100%;
  }

  .ui-react-select__value-container mark {
    background-color: transparent;
  }
`;

export function useSelectStyling(params: {
  hasLeft: boolean;
  outlined: boolean;
  error?: boolean;
  overrideStyles?: NamedProps['styles'];
}) {
  const theme = useTheme();

  return React.useMemo<NamedProps['styles']>(() => {
    return {
      control: (base, state) => {
        let styling;

        if (params.outlined) {
          styling = getOutlinedInputStateColors({
            theme,
            disabled: state.isDisabled,
            error: params.error,
          });
        } else {
          styling = getInputStateColors({
            theme,
            disabled: state.isDisabled,
            error: params.error,
          });
        }

        return {
          ...base,
          transition: 'all',
          transitionDuration: '0.2s',
          height: 40,
          borderStyle: 'solid',
          borderWidth: 2,
          borderRadius: theme.radius.small,
          ...theme.typography.body2.strong,
          outline: 'none',
          paddingLeft: params.hasLeft ? 0 : 16,
          boxShadow: 'none',

          '&:focused': {
            color: styling.focus.color,
            background: styling.focus.backgroundColor,
            borderColor: styling.focus.borderColor,
            outline: 'none',
            boxShadow: 'none',
          },
          ...(state.menuIsOpen
            ? {
                color: styling.focus.color,
                background: styling.focus.backgroundColor,
                borderColor: styling.focus.borderColor,

                '&:hover': {
                  color: styling.focus.color,
                  background: styling.focus.backgroundColor,
                  borderColor: styling.focus.borderColor,
                  boxShadow: 'none',
                },
              }
            : {
                borderColor: styling.base.borderColor,
                color: styling.base.color,
                background: styling.base.backgroundColor,

                '&:hover': {
                  color: styling.hover.color,
                  background: styling.hover.backgroundColor,
                  borderColor: styling.hover.borderColor,
                  boxShadow: 'none',
                },
              }),
          ...(state.isDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
          ...params.overrideStyles?.control?.(base, state),
        };
      },
      valueContainer: (base, state) => {
        return {
          ...base,
          cursor: 'pointer',
          ...theme.typography.body2.strong,
          padding: 0,
          color: theme.color.tint.primary,
          ...params.overrideStyles?.valueContainer?.(base, state),
        };
      },
      singleValue: (base, state) => ({
        ...base,
        background: 'transparent',
        color: state.data.value === '' ? theme.color.tint.tertiary : theme.color.tint.primary,
        ...theme.typography.body2.strong,
        ...params.overrideStyles?.singleValue?.(base, state),
      }),
      placeholder: (base, state) => ({
        ...base,
        color: theme.color.tint.tertiary,
        ...params.overrideStyles?.placeholder?.(base, state),
      }),
      clearIndicator: (base, state) => ({
        ...base,
        padding: '0px 8px',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        ...params.overrideStyles?.clearIndicator?.(base, state),
      }),
      dropdownIndicator: (base, state) => ({
        ...base,
        color: theme.color.tint.action,
        padding: '4px 12px',
        alignItems: 'center',
        justifyContent: 'center',
        transform: state.isFocused ? 'rotate(180deg)' : 'rotate(0deg)',
        transition: 'all 0.2s',
        cursor: 'pointer',
        ...params.overrideStyles?.dropdownIndicator?.(base, state),
      }),
      indicatorSeparator: (base, state) => ({
        ...base,
        backgroundColor:
          state.hasValue && state.selectProps.isClearable
            ? theme.color.border.emphasis
            : 'transparent',
        ...params.overrideStyles?.indicatorSeparator?.(base, state),
      }),
      multiValue: (base, state) => ({
        ...base,
        borderRadius: theme.radius.large,
        background: 'transparent',
        borderColor: theme.color.state.activeBorder,
        borderWidth: 1,
        borderStyle: 'solid',
        height: 28,
        alignItems: 'center',
        ...params.overrideStyles?.multiValue?.(base, state),
      }),
      multiValueLabel: (base, state) => ({
        ...base,
        color: theme.color.tint.action,
        paddingLeft: 12,
        ...theme.typography.overline.default,
        ...params.overrideStyles?.multiValueLabel?.(base, state),
      }),
      multiValueRemove: (base, state) => ({
        ...base,
        '&:hover': {
          background: 'transparent',
        },
        ...params.overrideStyles?.multiValueRemove?.(base, state),
      }),
      menu: (base, state) => ({
        ...base,
        marginTop: 0,
        paddingTop: theme.radius.small,
        paddingBottom: theme.radius.small,
        borderRadius: theme.radius.small,
        boxShadow: theme.shadows.medium,
        zIndex: 9999,
        ...params.overrideStyles?.menu?.(base, state),
      }),
      option: (base, state) => ({
        ...base,
        display: 'flex',
        alignItems: 'center',
        minHeight: 40,
        paddingLeft: 16,
        paddingRight: 16,
        ...theme.typography.overline.default,
        color: theme.color.tint.action,
        cursor: 'pointer',
        background: theme.color.surface.baseline,
        '&:hover': {
          backgroundColor: theme.createOverlayColor(theme.color.surface.baseline, 'hover'),
        },
        ...(state.isFocused && {
          backgroundColor: theme.createOverlayColor(theme.color.surface.baseline, 'hover'),
        }),
        ...(state.isSelected &&
          !state.isMulti && {
            color: theme.color.tint.selection,
            background: theme.color.surface.baseline,
          }),
        ...(state.isDisabled && { opacity: 0.5 }),
        ...params.overrideStyles?.option?.(base, state),
      }),
    };
  }, [params.hasLeft, params.hasLeft, params.outlined, params.error, params.overrideStyles]);
}
